/**
 * Header interaction behavior that was previously entirely absent: desktop
 * mega-menu hover-intent (open after a short delay, close after a longer
 * one so moving the mouse toward the panel doesn't close it), Escape to
 * dismiss any open nav item or the mobile drawer, a focus trap while the
 * mobile drawer is open (with focus restored to the button that opened it),
 * and the drawer's slide-in transition.
 *
 * DOM contract: desktop nav roots are `<details data-nav-item>` containing
 * a `[data-mega-panel]`; the mobile drawer is `.site-header__drawer`
 * (native `<details>`) containing `.site-header__drawer-panel`.
 */
const OPEN_DELAY = 120;
const CLOSE_DELAY = 240;

function initHoverIntent() {
  document.querySelectorAll('[data-nav-item]').forEach((item) => {
    let openTimer = null;
    let closeTimer = null;

    const clear = () => {
      window.clearTimeout(openTimer);
      window.clearTimeout(closeTimer);
    };

    item.addEventListener('pointerenter', () => {
      if (item.matches(':hover') === false) return;
      clear();
      openTimer = window.setTimeout(() => {
        document.querySelectorAll('[data-nav-item][open]').forEach((other) => {
          if (other !== item) other.open = false;
        });
        item.open = true;
      }, OPEN_DELAY);
    });

    item.addEventListener('pointerleave', () => {
      clear();
      closeTimer = window.setTimeout(() => {
        item.open = false;
      }, CLOSE_DELAY);
    });
  });
}

function closeAllNavItems() {
  document.querySelectorAll('[data-nav-item][open]').forEach((item) => {
    item.open = false;
  });
}

function initEscapeAndOutsideClick() {
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    closeAllNavItems();
    const drawer = document.querySelector('.site-header__drawer[open]');
    if (drawer) drawer.open = false;
  });

  document.addEventListener('click', (event) => {
    document.querySelectorAll('[data-nav-item][open]').forEach((item) => {
      if (!item.contains(event.target)) item.open = false;
    });
  });
}

/**
 * Native <details> gives Escape-to-close and click-toggle for free, but not
 * a focus trap or focus restoration - Tab would otherwise leak out to the
 * page behind the scrim, and closing the drawer would leave focus wherever
 * it happened to land.
 */
function initDrawerFocusTrap() {
  const drawer = document.querySelector('.site-header__drawer');
  const panel = drawer?.querySelector('.site-header__drawer-panel');
  if (!drawer || !panel) return;

  let opener = null;

  drawer.addEventListener('toggle', () => {
    if (drawer.open) {
      opener = document.activeElement;
      panel.querySelector('a, button')?.focus();
    } else {
      opener?.focus();
    }
  });

  panel.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab' || !drawer.open) return;

    const focusable = Array.from(
      panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), summary')
    ).filter((el) => el.offsetParent !== null);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
}

/**
 * Two-level push panel for the mobile drawer: a root list of top-level nav
 * items, and one sibling level per item that has a panel (built from the
 * same nav_item blocks as the desktop mega menu - see snippets/mega-menu.liquid
 * and the drawer markup in sections/header.liquid). Opening a level slides
 * it in from the right; "back" slides back to the root. Replaces the old
 * nested-accordion behavior, which read as an inline expand rather than a
 * distinct second screen.
 */
function initDrawerLevels() {
  const nav = document.querySelector('[data-drawer-nav]');
  if (!nav) return;

  const levels = nav.querySelectorAll('[data-drawer-level]');

  function showLevel(name) {
    levels.forEach((level) => {
      const isTarget = level.dataset.drawerLevel === name;
      level.hidden = !isTarget;
      level.classList.toggle('is-active', isTarget);
    });
  }

  nav.addEventListener('click', (event) => {
    const openTrigger = event.target.closest('[data-drawer-open-level]');
    if (openTrigger) {
      showLevel(openTrigger.dataset.drawerOpenLevel);
      nav.querySelector(`[data-drawer-level="${CSS.escape(openTrigger.dataset.drawerOpenLevel)}"] a, [data-drawer-level="${CSS.escape(openTrigger.dataset.drawerOpenLevel)}"] button`)?.focus();
      return;
    }

    if (event.target.closest('[data-drawer-back]')) {
      showLevel('root');
    }
  });

  document.querySelector('.site-header__drawer')?.addEventListener('toggle', function () {
    if (!this.open) showLevel('root');
  });
}

initHoverIntent();
initEscapeAndOutsideClick();
initDrawerFocusTrap();
initDrawerLevels();
